import React, { useState, useEffect } from 'react';
import ConversationList from '../components/inbox/ConversationList';
import ChatWindow from '../components/inbox/ChatWindow';
import ConversationDetails from '../components/inbox/ConversationDetails';
import NewConversationModal from '../components/inbox/NewConversationModal';
import SendQuotePrompt from '../components/inbox/SendQuotePrompt';
import { ChevronLeft } from 'lucide-react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useCompany } from '../context/CompanyContext';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';

const Inbox = () => {
  const [selectedConversation, setSelectedConversation] = useState(null);
  // Right panel is hidden by default; toggled via the Info button in ChatWindow header
  const [showDetails, setShowDetails] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { companyId } = useCompany();

  const [pendingQuote, setPendingQuote] = useState(null);

  // Handle deep link: /inbox?sendQuote=X&leadId=Y
  useEffect(() => {
    const sendQuoteId = searchParams.get('sendQuote');
    const leadId = searchParams.get('leadId');
    if (!sendQuoteId || !leadId || !companyId) return;

    const findConversation = async () => {
      try {
        const convoRef = collection(db, 'companies', companyId, 'conversations');
        const q = query(convoRef, where('anchorId', '==', leadId));
        const snapshot = await getDocs(q);
        if (snapshot.empty) {
          setPendingQuote({ quoteId: sendQuoteId, leadId, noConversation: true });
          return;
        }
        const convoDoc = snapshot.docs[0];
        const convoData = { id: convoDoc.id, ...convoDoc.data() };
        setSelectedConversation(convoData);
        setPendingQuote({ quoteId: sendQuoteId, leadId, conversation: convoData });
        setSearchParams({});
      } catch (err) {
        console.error('Failed to find conversation for quote:', err);
      }
    };
    findConversation();
  }, [searchParams, companyId, setSearchParams]);

  // Phase 1 / B2 — Deep link from notifications: /inbox?conversation={id}
  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    if (!conversationId || !companyId) return;
    if (selectedConversation?.id === conversationId) {
      // Already selected — just clean the URL
      setSearchParams({});
      return;
    }

    (async () => {
      try {
        const ref = doc(db, 'companies', companyId, 'conversations', conversationId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setSelectedConversation({ id: snap.id, ...(snap.data() || {}) });
        }
      } catch (err) {
        console.error('[Inbox] Failed to open conversation deep-link', err);
      } finally {
        setSearchParams({});
      }
    })();
  }, [searchParams, companyId, selectedConversation, setSearchParams]);

  // Global new-conversation event from ConversationList
  useEffect(() => {
    const handler = () => setShowNewConversation(true);
    window.addEventListener('inbox:new-conversation', handler);
    return () => window.removeEventListener('inbox:new-conversation', handler);
  }, []);

  const handleConversationCreated = (conversationId, clientPhone) => {
    if (location.pathname !== '/inbox') navigate('/inbox');
    setSelectedConversation({ id: conversationId, clientPhone });
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setShowDetails(false); // close panel on new conversation
  };

  const handleMobileBack = () => {
    setSelectedConversation(null);
    setShowDetails(false);
  };

  const handleToggleDetails = () => setShowDetails((v) => !v);

  return (
    <div className="flex h-[calc(100vh-4rem)] lg:-m-6 overflow-hidden bg-gray-50">

      {/* ── Conversation List ───────────────────────────────────────────────── */}
      <div
        className={`
          w-full sm:w-80 lg:w-96 border-r border-gray-200 bg-white flex-shrink-0 overflow-x-hidden
          ${selectedConversation ? 'hidden sm:flex sm:flex-col' : 'flex flex-col'}
        `}
        style={{ height: '100%', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <ConversationList
          selectedConversation={selectedConversation}
          onSelectConversation={handleSelectConversation}
          onStartNewConversation={() => window.dispatchEvent(new CustomEvent('inbox:new-conversation'))}
        />
      </div>

      <NewConversationModal
        open={showNewConversation}
        onClose={() => setShowNewConversation(false)}
        onConversationCreated={handleConversationCreated}
      />

      {/* ── Chat Window ─────────────────────────────────────────────────────── */}
      <div
        className={`
          flex-1 flex flex-col min-w-0 bg-white
          ${selectedConversation ? 'flex' : 'hidden sm:flex'}
        `}
      >
        {/* Mobile back button row */}
        {selectedConversation && (
          <div className="sm:hidden flex items-center gap-2 px-2 py-3 border-b border-gray-200 bg-white flex-shrink-0">
            <button
              onClick={handleMobileBack}
              className="p-2 -ml-1 text-gray-600 active:bg-gray-100 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Back to conversations"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <p className="flex-1 font-semibold text-[var(--cb-navy)] truncate">
              {selectedConversation.displayName || selectedConversation.clientName || selectedConversation.clientPhone || 'Conversation'}
            </p>
          </div>
        )}

        <div className="flex-1 overflow-hidden">
          <ChatWindow
            conversation={selectedConversation}
            hideMobileHeader={true}
            pendingQuote={pendingQuote}
            onQuoteSent={() => setPendingQuote(null)}
            onToggleDetails={handleToggleDetails}
            showDetails={showDetails}
            onResolve={() => setSelectedConversation(null)}
          />
        </div>

        {pendingQuote?.noConversation && (
          <SendQuotePrompt
            quoteId={pendingQuote.quoteId}
            leadId={pendingQuote.leadId}
            noConversation={true}
            onDismiss={() => { setPendingQuote(null); setSearchParams({}); }}
            onStartConversation={() => {
              setShowNewConversation(true);
              setPendingQuote(null);
              setSearchParams({});
            }}
          />
        )}
      </div>

      {/* ── Right Details Panel ─────────────────────────────────────────────── */}
      {/* Desktop: slide in from right; hidden by default, toggled via Info button */}
      {showDetails && selectedConversation && (
        <>
          {/* Desktop inline panel */}
          <div
            className="hidden sm:flex flex-col w-80 lg:w-96 flex-shrink-0 border-l border-gray-200 bg-white"
            data-testid="details-panel"
          >
            <ConversationDetails
              key={selectedConversation.id}
              conversation={selectedConversation}
              onClose={() => setShowDetails(false)}
            />
          </div>

          {/* Mobile overlay sheet */}
          <div className="sm:hidden fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setShowDetails(false)}
            />
            <div
              className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-xl"
              style={{
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                animation: 'slideInRight 0.2s ease-out',
              }}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="font-semibold text-[var(--cb-navy)]">Details</h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="p-2 text-gray-500 active:bg-gray-100 rounded-lg"
                >
                  <ChevronLeft className="h-5 w-5 rotate-180" />
                </button>
              </div>
              <div className="overflow-y-auto h-full">
                <ConversationDetails
                  conversation={selectedConversation}
                  onResolve={() => setSelectedConversation(null)}
                  onClose={() => setShowDetails(false)}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default Inbox;
